// src/components/buyerPersona/BuyerPersonaResult.jsx
import React, { useEffect } from 'react';
import { FaUser, FaMapMarkerAlt, FaChartLine, FaLightbulb, FaHeart, FaArrowLeft, FaTimes, FaUserTie, FaShoppingCart, FaStar, FaComments, FaCrosshairs, FaExclamationTriangle, FaBullseye, FaArrowRight, FaCheck } from 'react-icons/fa';
import { useBuyerPersonaImage } from '../../hooks/useBuyerPersonaImage';

const BuyerPersonaResult = ({
    result,
    onBack,
    onClose,
    showNextStepButton = false,
    nextStepButtonText = "Continuar"
}) => {
    const { imageUrl, imageStatus, isPolling, regenerateImage, getStatusMessage, getStatusColor, startPolling, checkImageStatus } = useBuyerPersonaImage();

    console.log('🎯 BuyerPersonaResult - Props recibidos:', {
        result,
        showNextStepButton,
        nextStepButtonText
    });

    console.log('🖼️ Estado de la imagen:', {
        imageUrl,
        imageStatus,
        isPolling,
        statusMessage: getStatusMessage(),
        statusColor: getStatusColor()
    });

    // Iniciar polling cuando el componente se monta
    useEffect(() => {
        const initializeImage = async () => {
            console.log('🚀 Iniciando verificación de imagen...');
            const status = await checkImageStatus();
            console.log('📊 Resultado de verificación:', status);

            if (!status?.imageUrl) {
                console.log('🔄 No hay imagen, iniciando polling...');
                startPolling();
            } else {
                console.log('✅ Imagen encontrada:', status.imageUrl);
            }
        };

        initializeImage();
    }, []);

    const handleRegenerateImage = async () => {
        try {
            console.log('🔄 Solicitando regeneración de imagen...');
            await regenerateImage();
            console.log('✅ Solicitud de regeneración enviada');
        } catch (error) {
            console.error('❌ Error regenerando imagen:', error);
        }
    };

    // Función para formatear los tonos de comunicación
    const formatTonos = (tonos) => {
        if (!tonos || typeof tonos !== 'string') return '';

        // Usar expresión regular para separar palabras que empiezan con mayúscula
        return tonos.split(/(?=[A-Z])/)
            .filter(tono => tono.length > 0) // Filtrar elementos vacíos
            .join(' • '); // Unir con bullet points
    };

    return (
        <div className="bg-gray-50 min-h-screen p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-800 mb-2">
                        Tu Buyer Persona
                    </h2>
                    <p className="text-gray-600">
                        Conoce a tu cliente ideal para crear mejores estrategias de marketing
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Columna Central - Nombre y Avatar */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center shadow-sm">
                            <h3 className="text-2xl font-bold text-gray-800 mb-6">
                                {result.nombre}
                            </h3>

                            {/* Avatar */}
                            <div className="flex items-center justify-center mb-6">
                                {imageUrl ? (
                                    <div className="relative">
                                        <img
                                            src={imageUrl}
                                            alt={result.nombre}
                                            className="w-32 h-32 rounded-full object-cover border-4 border-blue-200 shadow-lg"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                        <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-4xl font-bold border-4 border-blue-200 shadow-lg" style={{ display: 'none' }}>
                                            {result.nombre.charAt(0)}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-4xl font-bold border-4 border-blue-200 shadow-lg">
                                        {result.nombre.charAt(0)}
                                    </div>
                                )}
                            </div>

                            {/* Estado de la imagen */}
                            <div className="text-center">
                                <p className={`text-sm ${getStatusColor()}`}>
                                    {getStatusMessage()}
                                </p>
                            </div>
                        </div>

                        {/* Intereses */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6 shadow-sm">
                            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FaHeart className="mr-2 text-pink-500" />
                                Intereses
                            </h4>
                            <div className="space-y-2">
                                {result.contenidoPreferido?.map((interes, index) => (
                                    <div key={index} className="flex items-center">
                                        <span className="text-pink-500 mr-2">•</span>
                                        <span className="text-gray-700">{interes}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Columna Izquierda - Perfil, Demografía y Hábitos */}
                    <div className="lg:col-span-1">
                        {/* Perfil */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
                            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FaUserTie className="mr-2 text-blue-500" />
                                Perfil
                            </h4>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Ocupación:</span>
                                    <span className="font-medium">{result.ocupacion}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Situación:</span>
                                    <span className="font-medium">{result.situacionPersonal}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Nivel adquisitivo:</span>
                                    <span className="font-medium">{result.nivelAdquisitivo}</span>
                                </div>
                            </div>
                        </div>

                        {/* Datos Demográficos */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
                            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FaUser className="mr-2 text-green-500" />
                                Datos Demográficos
                            </h4>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Edad:</span>
                                    <span className="font-medium">{result.edadMedia}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Género:</span>
                                    <span className="font-medium">{result.genero}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Ubicación:</span>
                                    <span className="font-medium">{result.ubicacion}</span>
                                </div>
                            </div>
                        </div>

                        {/* Hábitos */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FaShoppingCart className="mr-2 text-purple-500" />
                                Hábitos
                            </h4>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Frecuencia de compra:</span>
                                    <span className="font-medium">{result.frecuenciaCompra}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Presupuesto:</span>
                                    <span className="font-medium">{result.presupuesto}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Tipo de cliente:</span>
                                    <span className="font-medium">{result.tipoCliente}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Nivel digital:</span>
                                    <span className="font-medium">{result.nivelDigitalizacion}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Columna Derecha - Retos, Frustraciones y Objetivos */}
                    <div className="lg:col-span-1">
                        {/* Retos */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
                            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FaCrosshairs className="mr-2 text-orange-500" />
                                Retos
                            </h4>
                            <div className="space-y-2">
                                {result.problemas?.map((problema, index) => (
                                    <div key={index} className="flex items-start">
                                        <span className="text-orange-500 mr-2 mt-1">•</span>
                                        <span className="text-gray-700">{problema}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Frustraciones */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
                            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FaExclamationTriangle className="mr-2 text-red-500" />
                                Frustraciones
                            </h4>
                            <div className="space-y-2">
                                {result.objeciones?.map((objecion, index) => (
                                    <div key={index} className="flex items-start">
                                        <span className="text-red-500 mr-2 mt-1">•</span>
                                        <span className="text-gray-700">{objecion}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Objetivos */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FaBullseye className="mr-2 text-green-500" />
                                Objetivos
                            </h4>
                            <div className="space-y-2">
                                {result.motivaciones?.map((motivacion, index) => (
                                    <div key={index} className="flex items-start">
                                        <span className="text-green-500 mr-2 mt-1">•</span>
                                        <span className="text-gray-700">{motivacion}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sección de Comunicación */}
                <div className="mt-8">
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                        <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <FaComments className="mr-2 text-blue-500" />
                            Estrategia de Comunicación
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h5 className="font-medium text-gray-700 mb-3">Canales de encuentro:</h5>
                                <div className="flex flex-wrap gap-2">
                                    {result.canalesEncuentro?.map((canal, index) => (
                                        <span
                                            key={index}
                                            className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                                        >
                                            {canal}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            {/* Sección de Tonos de Comunicación */}
                            {result.tonoComunicacion && typeof result.tonoComunicacion === 'string' && (
                                <div className="p-6 border-t border-gray-200">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                        Tono de comunicación
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {formatTonos(result.tonoComunicacion).split(' • ').filter(Boolean).map((tono, index) => (
                                            <span
                                                key={index}
                                                className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
                                            >
                                                {tono}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Recomendaciones */}
                {result.recomendaciones && result.recomendaciones.length > 0 && (
                    <div className="mt-8">
                        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FaStar className="mr-2 text-yellow-500" />
                                Recomendaciones
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {result.recomendaciones.map((recomendacion, index) => (
                                    <div key={index} className="flex items-start">
                                        <span className="text-yellow-500 mr-2 mt-1">•</span>
                                        <span className="text-gray-700">{recomendacion}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                {onBack && (
                    <button
                        onClick={onBack}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    >
                        Volver al Cuestionario
                    </button>
                )}
                <button
                    onClick={onClose}
                    className={`px-6 py-2 ${showNextStepButton ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white rounded-md transition-colors flex items-center`}
                >
                    {showNextStepButton ? (
                        <>
                            <span>{nextStepButtonText}</span>
                            <FaArrowRight className="ml-2" />
                        </>
                    ) : (
                        <>
                            <FaCheck className="mr-2" />
                            <span>Finalizar</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default BuyerPersonaResult; 